import { FileVariant } from '@/models/file.model';
import { Initiator } from '@/models/logs.model';
import { OrderStatus } from '@/models/order.model';
import { StepStatus, StepType } from '@/models/step.model';
import { IProgressStep, IPropertyOwner } from '@/models/property.model';
import { IViewingAttendee } from '@/models/viewing.model';
import { IContractSigner } from '@/models/contract.model';

export enum PropertyProgressEndpoints {
  ROOT = '/properties',
  PROGRESS = '/progress',
  STEP = '/steps',
  RESET = '/reset',
  PROPERTY_AD_STOP = '/advertising/stop',
}

export const getPropertyEndpoints = ({ propertyId, stepName }: { propertyId: string; stepName?: StepType }) => ({
  GET_PROPERTY_PROGRESS: `${PropertyProgressEndpoints.ROOT}/${propertyId}${PropertyProgressEndpoints.PROGRESS}`,
  PROPERTY_AD_STOP: `${PropertyProgressEndpoints.ROOT}/${propertyId}${PropertyProgressEndpoints.PROPERTY_AD_STOP}`,
  RESET_PROPERTY_STEP: `${PropertyProgressEndpoints.ROOT}/${propertyId}${PropertyProgressEndpoints.STEP}/${stepName}${PropertyProgressEndpoints.RESET}`,
});

export interface IUserStepBase {
  PK: string;
  SK: string;
  user_id: string;
  property_id: string;
  step_name: StepType;
  status: StepStatus;
  data_ordered_at?: string;
  data_uploaded_at?: string;
  created: string;
  modified: string;
}

export interface UserStepProgressOrder {
  PK: string;
  status: OrderStatus;
  createdAt: string;
  completedAt?: string;
}

export interface AppraisalProgress {
  condition_report_created_at?: string;
  rate_report_created_at?: string;
  price?: number;
  files?: AppraisalFile[];
}

export interface AppraisalFile {
  id: string;
  file_name: string;
  file_link: string;
  uploadedBy: Initiator;
  uploadedAt: string;
}

export interface PropertyPhotoProgress {
  PK: string;
  file_name: string;
  file_link: string;
  user_id: string;
  property_id: string;
  uploadedBy: Initiator;
  created: string;
  modified: string;
  file_type: FileVariant.PHOTO_INSIDE_PROPERTY | FileVariant.PHOTO_OUTSIDE_PROPERTY;
}

// Advertisement

export enum PropertyAdState {
  NOT_STARTED = 'not_started',
  DRAFT = 'draft',
  PUBLISHING = 'publishing',
  LIVE = 'live',
  LIVE_SOLD = 'live_sold',
  INACTIVE = 'inactive',
  FAILED = 'failed',
}

export enum AdInactiveReason {
  NEVER_PUBLISHED = 'never_published',
  SELLER_STOPPED = 'seller_stopped',
  DATA_EXPIRED = 'data_expired',
  EXPIRED = 'expired',
  SOLD = 'sold',
  REMOVED_BY_FINN = 'removed_by_finn',
}

export enum AdOperation {
  PREVIEW = 'preview',
  PUBLISH = 'publish',
  UPDATE = 'update',
  STOP = 'stop',
  RESUME = 'resume',
  MARK_SOLD = 'mark_sold',
}

export enum PropertyAdProblemOrigin {
  /** FINN's commit validation rejected it — the seller fixes the named field. */
  FINN_VALIDATION = 'finn_validation',
  /** Caught by AIBO before sending — typically legacy content. */
  AIBO_PRE_PUBLISH = 'aibo_pre_publish',
  /** The listing changed underneath AIBO — nothing to edit, retry. */
  CONFLICT = 'conflict',
}

export interface IPropertyAdProblem {
  field: string;
  index?: number | null;
  sent_value?: string | null;
  code: string;
  message: string;
  origin: PropertyAdProblemOrigin;
}

export interface IAdvertisementProgress {
  state: PropertyAdState;
  in_progress: AdOperation | null;
  inactive_reason: AdInactiveReason | null;
  can_resume: boolean;
  ad_url?: string;
  preview_url?: string;
  statistics_url?: string;
  publication_period_starts?: string;
  publication_period_ends?: string;
  publication_stopped_at?: string;
  problems: IPropertyAdProblem[];
  last_checked_at?: string;
}

export interface UserStepProgress extends IUserStepBase {
  order?: UserStepProgressOrder;
}

export interface AppraiserStepProgress extends UserStepProgress {
  step_name: StepType.APPRAISER;
  appraisal?: AppraisalProgress;
}

export interface AdStepProgress extends UserStepProgress {
  step_name: StepType.AD;
  advertisement?: IAdvertisementProgress;
}

export interface ContractStepProgress extends UserStepProgress {
  step_name: StepType.CONTRACT;
  signers?: IContractSigner[];
}

export interface SettlementStepProgress extends UserStepProgress {
  step_name: StepType.SETTLEMENT;
  signers?: IContractSigner[];
}

export interface PhotoshootingStepProgress extends UserStepProgress {
  step_name: StepType.PHOTOSHOOTING;
  photos_inside: PropertyPhotoProgress[];
  photos_outside: PropertyPhotoProgress[];
}

export interface ViewingStepProgress extends UserStepProgress {
  step_name: StepType.VIEW;
  signups: IViewingAttendee[];
}

export interface BiddingStepProgress extends UserStepProgress {
  step_name: StepType.BIDDING;
}

export interface DocumentsStepProgress extends UserStepProgress {
  step_name: StepType.DOCUMENTS;
  required_documents_count?: number;
}

export type StepsProgresses =
  | AppraiserStepProgress
  | PhotoshootingStepProgress
  | DocumentsStepProgress
  | ViewingStepProgress
  | BiddingStepProgress
  | AdStepProgress
  | ContractStepProgress
  | SettlementStepProgress;

// ----------------- DTOs ----------------

export interface IGetPropertyProgressDTO {
  propertyId: string;
}

export interface IResetPropertyStepDTO {
  propertyId: string;
  clearData?: boolean;
  stepName: StepType;
}

export interface IStopPropertyAdDTO {
  propertyId: string;
}

// ------------ Responses --------------

export interface IGetPropertyProgressResponse {
  property_owner: IPropertyOwner;
  progress: IProgressStep[];
  total_orders_sum: number;
  steps: Array<StepsProgresses>;
}

export type IResetPropertyStepResponse = IUserStepBase;

export type TPropertyAdActionResponse = IAdvertisementProgress;
